import React, { useCallback } from 'react';
import { UploadCloud } from 'lucide-react';

const FileUpload = ({ onDataLoaded }) => {
    const [status, setStatus] = React.useState('idle');
    const [message, setMessage] = React.useState('');

    const [clientName, setClientName] = React.useState('');

    const processFiles = useCallback(async (files) => {
        setStatus('uploading');
        setMessage('Sending files to server...');
        const formData = new FormData();
        formData.append('clientName', clientName);
        Array.from(files).forEach(file => {
            formData.append('files', file);
        });

        try {
            const response = await fetch('/api/upload', {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Server upload failed');
            }

            setStatus('success');
            setMessage(result.message || `Successfully uploaded files!`);
            onDataLoaded();

            setTimeout(() => {
                setStatus('idle');
                setMessage('');
                setClientName('');
            }, 3000);

        } catch (error) {
            console.error("Error uploading files:", error);
            setStatus('error');
            setMessage(error.message || 'An unexpected error occurred.');
        }
    }, [onDataLoaded, clientName]);



    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFiles(files);
        }
    }, [processFiles]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            processFiles(files);
        }
    };

    return (
        <div className="w-full space-y-4">
            <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Client Name</label>
                <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="e.g. Client A"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={status === 'uploading'}
                />
            </div>

            <div
                className={`flex flex-col items-center justify-center p-10 border-2 border-dashed rounded-lg transition-colors cursor-pointer shadow-sm
                    ${status === 'error' ? 'border-red-400 bg-red-50' :
                        status === 'success' ? 'border-green-400 bg-green-50' :
                            'border-gray-300 hover:border-primary bg-white'}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            >
                <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    multiple
                    onChange={handleChange}
                    className="hidden"
                    id="file-upload"
                    disabled={status === 'uploading'}
                />
                <label htmlFor="file-upload" className="flex flex-col items-center cursor-pointer w-full">
                    {status === 'uploading' ? (
                        <div className="animate-pulse flex flex-col items-center">
                            <UploadCloud className="w-12 h-12 text-blue-500 mb-4" />
                            <p className="text-lg font-medium text-blue-600">{message}</p>
                        </div>
                    ) : status === 'success' ? (
                        <div className="flex flex-col items-center">
                            <UploadCloud className="w-12 h-12 text-green-500 mb-4" />
                            <p className="text-lg font-medium text-green-600">{message}</p>
                        </div>
                    ) : status === 'error' ? (
                        <div className="flex flex-col items-center text-center">
                            <UploadCloud className="w-12 h-12 text-red-500 mb-4" />
                            <p className="text-lg font-medium text-red-600 mb-2">Upload Failed</p>
                            <p className="text-sm text-red-500">{message}</p>
                            <p className="text-xs text-gray-400 mt-4">Click to try again</p>
                        </div>
                    ) : (
                        <>
                            <UploadCloud className="w-12 h-12 text-gray-400 mb-4" />
                            <p className="text-lg font-medium text-gray-700">Click to upload or drag and drop</p>
                            <p className="text-sm text-gray-500 mt-1">Excel (.xlsx, .xls) and CSV files supported</p>
                            <p className="text-xs text-gray-400 mt-2 text-center max-w-sm">
                                Required: Site Location Name, Latitude, Longitude<br />
                                Optional: Address, Zip, Symbol, Color, Size
                            </p>
                        </>
                    )}
                </label>
            </div>
            <div className="flex justify-end mt-2">
                <button 
                    onClick={(e) => {
                        e.preventDefault();
                        const csvContent = "data:text/csv;charset=utf-8,Client Name,Site Name,Latitude,Longitude,Address,Postal Code,System Size\nExample Client,Example Site 1,34.0522,-118.2437,123 Main St,90001,15\n";
                        const encodedUri = encodeURI(csvContent);
                        const link = document.createElement("a");
                        link.setAttribute("href", encodedUri);
                        link.setAttribute("download", "AGS_Site_Upload_Template.csv");
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }}
                    className="text-sm text-[#76AB3F] hover:text-[#5c8a31] font-medium underline transition-colors"
                >
                    Download Template (CSV)
                </button>
            </div>
        </div>
    );
};

export default FileUpload;
